/**
 * Table service (Supabase-backed).
 *
 * File kept as `firebaseTableService` for compatibility with the dozens
 * of existing imports; internally it now talks to the Supabase
 * `restaurant_tables` table. All public methods preserve the original
 * shape so callers don't need to change.
 */

import { supabase } from './supabaseClient';

export interface FirebaseTable {
  id?: string;
  number: number;
  status: 'available' | 'occupied' | 'reserved' | 'cleaning';
  capacity: number;
  area: 'indoor' | 'outdoor';
  customer_name?: string | null;
  total_amount?: number | null;
  item_count?: number | null;
  start_time?: string | null;
  end_time?: string | null;
  order_id?: string | null;
  tab_orders?: string[] | null;
  tab_total?: number | null;
  tab_item_count?: number | null;
  tab_rounds?: number | null;
  tab_open?: boolean | null;
  active_order_number?: string | null;
  occupied_by?: string | null;
  split_payment_complete?: boolean | null;
  split_people?: any[] | null;
  split_total?: number | null;
  split_session_id?: string | null;
  created_at: string;
  updated_at: string;
}

const COLS = 'id, number, status, capacity, area, customer_name, total_amount, item_count, start_time, end_time, order_id, tab_orders, tab_total, tab_item_count, tab_rounds, tab_open, active_order_number, occupied_by, split_payment_complete, split_people, split_total, split_session_id, created_at, updated_at';

class FirebaseTableService {
  private static instance: FirebaseTableService;

  public static getInstance(): FirebaseTableService {
    if (!FirebaseTableService.instance) {
      FirebaseTableService.instance = new FirebaseTableService();
    }
    return FirebaseTableService.instance;
  }

  public async createTable(tableData: Omit<FirebaseTable, 'id' | 'created_at' | 'updated_at'>): Promise<FirebaseTable> {
    const { data, error } = await supabase
      .from('restaurant_tables')
      .insert({ ...tableData })
      .select(COLS)
      .single();
    if (error) throw error;
    return data as FirebaseTable;
  }

  public async getTables(): Promise<FirebaseTable[]> {
    const { data, error } = await supabase
      .from('restaurant_tables')
      .select(COLS)
      .order('number', { ascending: true });
    if (error) throw error;
    return (data ?? []) as FirebaseTable[];
  }

  public async updateTableStatus(
    tableId: string,
    status: FirebaseTable['status'],
    extra?: Partial<FirebaseTable>,
  ): Promise<void> {
    const patch: any = { status, updated_at: new Date().toISOString(), ...(extra || {}) };
    const { error } = await supabase.from('restaurant_tables').update(patch).eq('id', tableId);
    if (error) throw error;
  }

  public async updateTableByNumber(
    tableNumber: number,
    status: FirebaseTable['status'],
    extra?: Partial<FirebaseTable>,
    area?: 'indoor' | 'outdoor',
  ): Promise<void> {
    let q = supabase
      .from('restaurant_tables')
      .update({ status, updated_at: new Date().toISOString(), ...(extra || {}) })
      .eq('number', tableNumber);
    if (area) q = q.eq('area', area);
    const { error } = await q;
    if (error) throw error;
  }

  public async deleteTable(tableId: string): Promise<void> {
    const { error } = await supabase.from('restaurant_tables').delete().eq('id', tableId);
    if (error) throw error;
  }

  public async deleteAllTables(): Promise<number> {
    const { data: existing } = await supabase.from('restaurant_tables').select('id');
    const count = (existing ?? []).length;
    const { error } = await supabase.from('restaurant_tables').delete().not('id', 'is', null);
    if (error) throw error;
    return count;
  }

  /**
   * Subscribe to live table changes. Returns an unsubscribe function.
   * Re-fetches the full list on any change — simpler than maintaining
   * a diff state.
   */
  public onTablesChange(callback: (tables: FirebaseTable[]) => void): () => void {
    let cancelled = false;
    const reload = async () => {
      try {
        const tables = await this.getTables();
        if (!cancelled) callback(tables);
      } catch (err) {
        console.error('[TABLES] reload failed:', err);
      }
    };
    void reload();
    const channel = supabase
      .channel(`tables_${Date.now()}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'restaurant_tables' }, () => { void reload(); })
      .subscribe();
    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }

  public async openTab(tableId: string, customerName?: string): Promise<void> {
    const patch: any = {
      tab_open: true,
      tab_orders: [],
      tab_total: 0,
      tab_item_count: 0,
      tab_rounds: 0,
      start_time: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    if (customerName) patch.customer_name = customerName;
    const { error } = await supabase.from('restaurant_tables').update(patch).eq('id', tableId);
    if (error) throw error;
  }

  public async addOrderToTab(tableId: string, orderId: string, orderTotal: number, itemCount: number, orderNumber?: string): Promise<void> {
    const { data: row, error: getErr } = await supabase
      .from('restaurant_tables')
      .select('tab_orders, tab_total, tab_item_count, tab_rounds')
      .eq('id', tableId)
      .maybeSingle();
    if (getErr) throw getErr;
    const orders: string[] = Array.isArray((row as any)?.tab_orders) ? (row as any).tab_orders : [];
    const newOrders = orders.includes(orderId) ? orders : [...orders, orderId];
    const patch: any = {
      tab_orders: newOrders,
      tab_total: Number((row as any)?.tab_total || 0) + (Number(orderTotal) || 0),
      tab_item_count: Number((row as any)?.tab_item_count || 0) + (Number(itemCount) || 0),
      tab_rounds: Number((row as any)?.tab_rounds || 0) + 1,
      updated_at: new Date().toISOString(),
    };
    if (orderNumber) patch.active_order_number = orderNumber;
    const { error } = await supabase.from('restaurant_tables').update(patch).eq('id', tableId);
    if (error) throw error;
  }

  public async closeTab(tableId: string): Promise<{ orderIds: string[]; total: number }> {
    const { data: row, error: getErr } = await supabase
      .from('restaurant_tables')
      .select('tab_orders, tab_total')
      .eq('id', tableId)
      .maybeSingle();
    if (getErr) throw getErr;
    const orderIds: string[] = Array.isArray((row as any)?.tab_orders) ? (row as any).tab_orders : [];
    const total = Number((row as any)?.tab_total || 0);
    const { error } = await supabase
      .from('restaurant_tables')
      .update({
        status: 'available',
        tab_open: false,
        tab_orders: [],
        tab_total: 0,
        tab_item_count: 0,
        tab_rounds: 0,
        customer_name: null,
        active_order_number: null,
        order_id: null,
        end_time: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', tableId);
    if (error) throw error;
    return { orderIds, total };
  }

  public async getTableById(tableId: string): Promise<FirebaseTable | null> {
    const { data, error } = await supabase
      .from('restaurant_tables')
      .select(COLS)
      .eq('id', tableId)
      .maybeSingle();
    if (error) throw error;
    return (data as FirebaseTable) || null;
  }

  public async findTable(tableNumber: number, area?: 'indoor' | 'outdoor'): Promise<FirebaseTable | null> {
    let q = supabase.from('restaurant_tables').select(COLS).eq('number', tableNumber).limit(1);
    if (area) q = q.eq('area', area);
    const { data, error } = await q.maybeSingle();
    if (error) throw error;
    return (data as FirebaseTable) || null;
  }

  /**
   * Seed the standard 20 indoor + 20 outdoor four-seater tables when the
   * tenant has none yet. Idempotent — does nothing if any tables exist.
   */
  public async initializeDefaultTables(): Promise<void> {
    const existing = await this.getTables();
    if (existing.length > 0) return;
    const rows: any[] = [];
    for (let n = 1; n <= 20; n += 1) {
      rows.push({ number: n, status: 'available', capacity: 4, area: 'indoor' });
      rows.push({ number: n, status: 'available', capacity: 4, area: 'outdoor' });
    }
    const { error } = await supabase.from('restaurant_tables').insert(rows);
    if (error) throw error;
  }
}

export const firebaseTableService = FirebaseTableService.getInstance();
export default FirebaseTableService;

/**
 * Compat shim — older callers (e.g. SplitTableDialog) imported this as a
 * named function. Marks the matching `restaurant_tables` row(s) as
 * split-paid + records the session payload. Idempotent.
 */
export async function markTableSplitPaid(
  tableNumber: number,
  payload: { split_session_id: string; split_people: any[]; split_total: number },
): Promise<void> {
  const { error } = await supabase
    .from('restaurant_tables')
    .update({
      split_payment_complete: true,
      split_session_id: payload.split_session_id,
      split_people: payload.split_people,
      split_total: payload.split_total,
      updated_at: new Date().toISOString(),
    })
    .eq('number', tableNumber);
  if (error) throw error;
}
